import React, { Fragment, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

import { mapProps } from '../../../../../../../util/hocs';
import { createLoadMoreMethod } from '../../../../../../../util/apollo';
import Placeholder from '../../../../../../pro/views/HierarchyItem/Matches/components/Placeholder';
import PhotoGrid from '../../../../../../../components/grid/PhotoGrid';
import { membershipsCartContainsPhoto } from '../../../../../../pro/views/HierarchyItem/Matches/helpers';
import getAlbumMembershipGroupIdentity from '../../graphql/queries/getAlbumMembershipGroupIdentity';
import getAlbumMembershipGroupWithAllPhotos from '../../graphql/queries/getAlbumMembershipGroupWithAllPhotos';
import {
  createAlbumMembershipGroupEntity,
  getAlbumMembershipGroupWebGalleryPrice,
} from '../../../../../../pro/views/HierarchyItem/Matches/components/PhotoPurchasingOptions/helpers';
import {
  openModal as openModalAction,
  closeModal as closeModalAction,
} from '../../../../../../../actions/modal-actions';
import { REPORT_PHOTO_MODAL } from '../../../../../../../constants/modals';
import {
  albumMembershipGroupAllowsPublicPhotoAttribution,
  albumMembershipGroupHasSocialShareEnabled,
  getAlbumMembershipGroupEntityAccount,
  getAlbumMembershipGroupHashcode,
  isPictureDayMembership,
  isWebGalleryMembership,
} from '../../helpers';
import { canReportPhotos } from '../../../../../../../util/product-permissions';
import MatchedPhoto from '../../../../../../pro/views/HierarchyItem/Matches/components/MatchedPhoto';
import MatchedPhotoActions from '../../../../../../pro/views/HierarchyItem/Matches/components/MatchedPhoto/MatchedPhotoActions';
import getAlbumMembershipGroupPurchases from '../../graphql/queries/getAlbumMembershipGroupPurchases';
import { PurchasingOptionsModal } from '../../../../../../pro/views/HierarchyItem/Matches/components/PurchasingOptions';
import { getAlbumMembershipGroupGreenScreenStatus } from '../../../../../../pro/views/HierarchyItem/Matches/components/PurchasingOptions/helpers';
import MatchesHeadMeta from '../../../../../../pro/views/HierarchyItem/Matches/components/MatchesHeadMeta';
import MatchedPhotoPhotoViewerActions from '../../../../../../pro/views/HierarchyItem/Matches/components/MatchedPhoto/MatchedPhotoPhotoViewerActions';
import PhotoGridViewerFooter from '../../../../../../pro/views/Album/AlbumPhotos/components/PhotoGridViewerFooter';
import useAlbumMembershipGroupPurchasingOptionsCheck from '../../../../../../pro/views/HierarchyItem/Matches/hooks/useAlbumMembershipGroupPurchasingOptionsCheck';
import useAlbumMembershipGroupPrintBackgrounds from '../../../../../../pro/views/HierarchyItem/Matches/hooks/useAlbumMembershipGroupPrintBackgrounds';
import { getAlbumMembershipGroupAlbum, photoIsEntitled } from './helpers';
import useFirstAvailableIdentity from '../../../../../../pro/views/HierarchyItem/All/components/AllPhotos/hooks/useFirstAvailableIdentity';
import Hierarchy from '../../../../../../pro/views/HierarchyItem/All/components/AllPhotos/components/Hierarchy';
import getAlbumForWebGalleriesPurchases from '../../graphql/queries/getAlbumForWebGalleriesPurchases';
import getAlbumForWebGalleriesAllPhotos from '../../graphql/queries/getAlbumForWebGalleriesAllPhotos';
import usePurchasesPendingConfiguration from '../../../../../../pro/views/HierarchyItem/Matches/components/PurchasesPendingConfiguration/hooks/usePurchasesPendingConfiguration';
import { ALL_PHOTOS_MODE } from '../../../../../../../constants/album-folder';
import { PURCHASES_PENDING_CONFIGURATION_MODAL } from '../../../../../../pro/views/HierarchyItem/Matches/components/PurchasesPendingConfiguration';
import GlobalWaldogramConfiguratorModal, {
  GLOBAL_WALDOGRAM_CONFIGURATOR_MODAL,
} from '../../../../../../pro/views/AccountAddressBook/components/GlobalWaldogramConfigurator/modal';
import ImagePreload from '../../../../../../../components/global/ImagePreload';
import NoUploadsNullState from '../../../../../../pro/views/HierarchyItem/Matches/components/NoUploadsNullState';
import DigitalBundlesMissingConfigurationNotice from '../../../../../../pro/views/HierarchyItem/Matches/components/DigitalBundlesMissingConfigurationNotice';
import { extractWatermarkSettings } from '../../../../../../../util/watermark-settings';
import getAlbumForWebGalleriesFolder from '../../graphql/queries/getAlbumForWebGalleriesFolder';
import { getHierarchyItemForMatches } from '../../../../../../pro/views/HierarchyItem/Matches/graphql/queries/hierarchy-item';

const propTypes = {
  albumMembershipGroup: PropTypes.shape({
    album: PropTypes.shape({
      id: PropTypes.string.isRequired,
      print_packages: PropTypes.shape({
        total: PropTypes.number,
        records: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
          }),
        ),
      }),
    }),
  }),
  album: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  cart: PropTypes.array.isRequired,
  loadMore: PropTypes.func,
  loading: PropTypes.bool,
  loadingAlbum: PropTypes.bool,
  loadingAlbumAlbumFolder: PropTypes.bool,
  openModal: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  membershipEntitledPurchases: PropTypes.arrayOf(PropTypes.string),
  albumEntitledPurchases: PropTypes.arrayOf(PropTypes.string),
  matchedPhotoPrintBackgrounds: PropTypes.object,
  closeModal: PropTypes.func,
  hierarchyItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
    album_folder: PropTypes.shape({
      id: PropTypes.string.isRequired,
      children: PropTypes.shape({
        album_folders: PropTypes.shape({
          total: PropTypes.number,
          records: PropTypes.arrayOf(PropTypes.object),
        }),
        albums: PropTypes.shape({
          total: PropTypes.number,
          records: PropTypes.arrayOf(PropTypes.object),
        }),
        __typename: PropTypes.string,
      }),
      root: PropTypes.object,
    }),
  }),
};

const AllPhotos = ({
  loadMore,
  loading,
  albumMembershipGroup,
  loadingAlbum,
  loadingAlbumAlbumFolder,
  album,
  photos,
  cart,
  openModal,
  history,
  membershipEntitledPurchases,
  albumEntitledPurchases,
  matchedPhotoPrintBackgrounds,
  closeModal,
  hierarchyItem,
}) => {
  useFirstAvailableIdentity({
    albumMembershipGroupId: albumMembershipGroup?.id,
  });
  const { activeIdentity } = useSelector(({ checkout }) => checkout);
  const entitledPurchases =
    membershipEntitledPurchases ?? albumEntitledPurchases ?? [];
  const [selectedMatchedPhoto, setSelectedMatchedPhoto] = useState(null);
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const hierarchyItemId = albumMembershipGroup?.is_album
    ? albumMembershipGroup.album.root_album_folder?.id
    : (albumMembershipGroup?.id ?? album?.root_album_folder?.id);
  const {
    hasPurchasedPrintPackages,
    prepaidPrintPackagesMissingConfiguration,
    prepaidPhotoPricingsMissingConfiguration,
    prepaidCustomPrintsMissingConfiguration,
  } = usePurchasesPendingConfiguration({
    hierarchyItemId,
  });

  const itemsMissingConfigurationTotal =
    (prepaidPrintPackagesMissingConfiguration?.length ?? 0) +
    (prepaidPhotoPricingsMissingConfiguration?.length ?? 0) +
    (prepaidCustomPrintsMissingConfiguration?.length ?? 0);

  const albumMembershipGroupEntity = createAlbumMembershipGroupEntity(
    albumMembershipGroup,
    album,
  );
  const vendorAccount = getAlbumMembershipGroupEntityAccount(
    albumMembershipGroupEntity,
  );
  const { sell_prints_enabled: sellPrintsEnabled = true } =
    vendorAccount?.pro_settings ?? {};

  const watermarkSettings = useMemo(
    () => extractWatermarkSettings(vendorAccount),
    [vendorAccount],
  );

  const { hasPurchasingOptions, loading: loadingPurchasingOptionsCheck } =
    useAlbumMembershipGroupPurchasingOptionsCheck({
      albumMembershipGroupId: albumMembershipGroup?.id,
      albumId: album && !albumMembershipGroup ? album?.id : undefined,
      sellPrintsEnabled,
    });

  const isGreenScreenEnabled = getAlbumMembershipGroupGreenScreenStatus(
    albumMembershipGroupEntity,
  );

  const { printBackgrounds } = useAlbumMembershipGroupPrintBackgrounds({
    albumMembershipGroupId: albumMembershipGroup?.id,
    albumId: album && !albumMembershipGroup ? album?.id : undefined,
    isGreenScreenEnabled,
  });

  const printBackgroundUrls = useMemo(
    () => printBackgrounds.map(background => background.asset_image.url),
    [printBackgrounds],
  );

  useEffect(() => {
    if (
      album?.has_all_photos_access === false &&
      album?.root_album_folder?.picture_day_enabled &&
      album?.root_album_folder?.all_photos_mode === ALL_PHOTOS_MODE.PRIVATE
    ) {
      history.push(`/s/all-photos-access/${album.id}`);
    } else if (
      albumMembershipGroup?.album_memberships_has_all_photos_access === false ||
      album?.has_all_photos_access === false
    ) {
      history.push(`/consumer/matches/${(albumMembershipGroup ?? album).id}`);
    } else if (hierarchyItem?.album_folder?.children?.albums?.total === 1) {
      history.push(
        `/consumer/matches/${hierarchyItem?.album_folder?.children?.albums?.records?.[0]?.id}/all`,
      );
    } else if (
      hierarchyItem?.album_folder?.children?.album_folders?.total === 1
    ) {
      history.push(
        `/consumer/matches/${hierarchyItem?.album_folder?.children?.album_folders?.records?.[0]?.id}/all`,
      );
    }
  }, [albumMembershipGroup, album, hierarchyItem]);

  if (
    loading ||
    loadingAlbum ||
    loadingAlbumAlbumFolder ||
    loadingPurchasingOptionsCheck
  ) {
    return <Placeholder />;
  }

  if ((albumMembershipGroup && !albumMembershipGroup.is_album) || !album) {
    return (
      <Fragment>
        <MatchesHeadMeta albumMembershipGroup={albumMembershipGroup} />
        <Hierarchy
          className="px-3 py-2"
          albumMembershipGroup={albumMembershipGroup}
          entitledPurchases={entitledPurchases}
          generateLink={albumMembershipGroupId =>
            `/consumer/matches/${albumMembershipGroupId}/all`
          }
        />
      </Fragment>
    );
  }

  const albumFolderId = album?.root_album_folder?.id;
  const allPhotos = albumMembershipGroup?.all_photos ?? photos;

  const webGalleryPrice = getAlbumMembershipGroupWebGalleryPrice(
    albumMembershipGroupEntity,
  );
  const socialShareEnabled = albumMembershipGroupHasSocialShareEnabled(
    albumMembershipGroupEntity,
  );
  const hashCode = getAlbumMembershipGroupHashcode(albumMembershipGroupEntity);

  const isPictureDay = isPictureDayMembership(albumMembershipGroupEntity);
  const isWebGallery = isWebGalleryMembership(albumMembershipGroupEntity);

  if (allPhotos?.total === 0) {
    return (
      <Fragment>
        <MatchesHeadMeta albumMembershipGroup={albumMembershipGroup} />
        <NoUploadsNullState />
      </Fragment>
    );
  }

  return (
    <Fragment>
      <MatchesHeadMeta albumMembershipGroup={albumMembershipGroup} />
      <div className="matched-photos-wrap">
        <PhotoGrid
          records={allPhotos?.records ?? []}
          loadMore={loadMore}
          total={allPhotos?.total ?? 0}
          targetRowHeight={isMobile ? 300 : 380}
          justifiedLayoutOptions={{
            targetRowHeightTolerance: isMobile ? 0.5 : 0.175,
            widowLayoutStyle: isMobile ? 'justify' : 'left',
          }}
          nativePhotoViewer
          photoViewerProps={{
            renderPhoto: ({ record: photo }) => {
              const photoAlbum =
                album ??
                getAlbumMembershipGroupAlbum(albumMembershipGroup, photo);
              const isEntitled = photoIsEntitled(
                albumMembershipGroup,
                entitledPurchases,
                photo,
              );
              const albumMembershipPhoto = {
                id: photo.id,
                photo,
                album: photoAlbum,
              };

              return (
                <MatchedPhoto
                  isAllPhotos
                  withWatermark
                  key={albumMembershipPhoto.id}
                  printBackgrounds={printBackgrounds}
                  matchedPhoto={albumMembershipPhoto}
                  isPictureDay={isPictureDay}
                  isEntitled={isEntitled}
                  displayAttribution={albumMembershipGroupAllowsPublicPhotoAttribution(
                    albumMembershipGroup,
                  )}
                  watermarkSettings={watermarkSettings}
                  displayWallpaper
                />
              );
            },
            actions: ({ record: photo }) => {
              const photoAlbum =
                album ??
                getAlbumMembershipGroupAlbum(albumMembershipGroup, photo);

              const isEntitled = photoIsEntitled(
                albumMembershipGroup,
                entitledPurchases,
                photo,
              );

              // Id is required by matched photo, so pass in the photo id as id.
              const albumMembershipPhoto = {
                id: photo.id,
                photo,
                album: photoAlbum,
              };

              return (
                <MatchedPhotoPhotoViewerActions
                  isAllPhotos={!isPictureDay || isWebGallery}
                  matchedPhoto={albumMembershipPhoto}
                  isEntitled={isEntitled}
                  onPhotoItemPurchase={() =>
                    setSelectedMatchedPhoto(albumMembershipPhoto)
                  }
                  canPurchasePhotoItems={hasPurchasingOptions}
                  canReportPhoto={canReportPhotos(albumMembershipPhoto.album)}
                  hasCartItems={membershipsCartContainsPhoto(
                    cart,
                    albumMembershipPhoto.photo.id,
                  )}
                  onPhotoReport={() =>
                    openModal(REPORT_PHOTO_MODAL, {
                      photoId: albumMembershipPhoto.photo.id,
                      albumId: albumMembershipPhoto.album?.id,
                    })
                  }
                  socialShareEnabled={socialShareEnabled}
                  hashCode={hashCode}
                  openModal={openModal}
                  onPhotoItemWaldogramPurchase={
                    !sellPrintsEnabled
                      ? undefined
                      : () => {
                          openModal(GLOBAL_WALDOGRAM_CONFIGURATOR_MODAL, {
                            photoId: albumMembershipPhoto.photo.id,
                          });
                        }
                  }
                  onPhotoItemCampergramPurchase={
                    albumMembershipPhoto?.album?.custom_prints_meta
                      ?.onsite_delivery_enabled
                      ? () =>
                          history.push(
                            `/app/shutterbug/hierarchy-items/${albumMembershipPhoto.album.id}/postcard?photoId=${albumMembershipPhoto.photo.id}`,
                          )
                      : undefined
                  }
                  albumFolderId={albumFolderId}
                  sellPhotosModeEnabled={isPictureDay}
                />
              );
            },
            footer: ({ record: photo }) => {
              const photoAlbum =
                album ??
                getAlbumMembershipGroupAlbum(albumMembershipGroup, photo);

              return (
                <PhotoGridViewerFooter entity={photoAlbum} photo={photo} />
              );
            },
          }}
          cellRenderer={({ record: photo, recordPath }) => {
            const photoAlbum =
              album ??
              getAlbumMembershipGroupAlbum(albumMembershipGroup, photo);

            const isEntitled = photoIsEntitled(
              albumMembershipGroup,
              entitledPurchases,
              photo,
            );
            const albumMembershipPhoto = {
              id: photo.id,
              photo,
              album: photoAlbum,
            };

            return (
              <MatchedPhoto
                isAllPhotos
                linkTo={recordPath}
                key={albumMembershipPhoto.id}
                printBackgrounds={printBackgrounds}
                isPictureDay={isPictureDay}
                matchedPhoto={albumMembershipPhoto}
                isEntitled={isEntitled}
                actions={
                  <MatchedPhotoActions
                    isAllPhotos={!isPictureDay}
                    matchedPhoto={albumMembershipPhoto}
                    isEntitled={isEntitled}
                    onPhotoItemPurchase={() =>
                      setSelectedMatchedPhoto(albumMembershipPhoto)
                    }
                    hasUnconfiguredPrePurchases={
                      itemsMissingConfigurationTotal > 0
                    }
                    onConfigurePrepaidItems={() =>
                      openModal(PURCHASES_PENDING_CONFIGURATION_MODAL, {
                        albumMembershipPhoto: {
                          ...albumMembershipPhoto,
                          print_background: printBackgrounds?.find(
                            background =>
                              background.id ===
                              matchedPhotoPrintBackgrounds?.[
                                albumMembershipPhoto?.id
                              ],
                          ),
                        },
                        identityId: activeIdentity?.id,
                        watermarkSettings,
                      })
                    }
                    canPurchasePhotoItems={hasPurchasingOptions}
                    canReportPhoto={canReportPhotos(albumMembershipPhoto.album)}
                    hasCartItems={membershipsCartContainsPhoto(
                      cart,
                      albumMembershipPhoto.photo.id,
                    )}
                    onPhotoReport={() =>
                      openModal(REPORT_PHOTO_MODAL, {
                        photoId: albumMembershipPhoto.photo.id,
                        albumId: albumMembershipPhoto.album?.id,
                      })
                    }
                    socialShareEnabled={socialShareEnabled}
                    hashCode={hashCode}
                    openModal={openModal}
                    linkTo={recordPath}
                    onPhotoItemWaldogramPurchase={
                      !sellPrintsEnabled
                        ? undefined
                        : () => {
                            openModal(GLOBAL_WALDOGRAM_CONFIGURATOR_MODAL, {
                              photoId: albumMembershipPhoto.photo.id,
                            });
                          }
                    }
                    onPhotoItemCampergramPurchase={
                      albumMembershipPhoto?.album?.custom_prints_meta
                        ?.onsite_delivery_enabled
                        ? () =>
                            history.push(
                              `/app/shutterbug/hierarchy-items/${albumMembershipPhoto.album.id}/postcard?photoId=${albumMembershipPhoto.photo.id}`,
                            )
                        : undefined
                    }
                    sellPhotosModeEnabled={isPictureDay}
                  />
                }
                displayAttribution={albumMembershipGroupAllowsPublicPhotoAttribution(
                  albumMembershipGroup,
                )}
                watermarkSettings={watermarkSettings}
                displayWallpaper
              />
            );
          }}
        />

        {selectedMatchedPhoto && (
          <PurchasingOptionsModal
            albumFolderId={album?.root_album_folder?.id}
            hierarchyItemId={albumMembershipGroup?.id ?? album?.id}
            album={
              album ??
              getAlbumMembershipGroupAlbum(
                albumMembershipGroup,
                selectedMatchedPhoto.photo,
              )
            }
            forceOpen={!!selectedMatchedPhoto}
            photo={{
              ...selectedMatchedPhoto.photo,
              print_background: printBackgrounds?.find(
                background =>
                  background.id ===
                  matchedPhotoPrintBackgrounds?.[selectedMatchedPhoto?.id],
              ),
            }}
            onClose={() => setSelectedMatchedPhoto(null)}
            isPhotoEntitled={photoIsEntitled(
              albumMembershipGroup,
              entitledPurchases,
              selectedMatchedPhoto.photo,
            )}
            entitledPurchases={entitledPurchases}
            webGalleryPrice={webGalleryPrice}
            hasPurchasedPrintPackages={hasPurchasedPrintPackages}
          />
        )}
        <DigitalBundlesMissingConfigurationNotice
          isAllPhotos
          albumMembershipGroup={albumMembershipGroupEntity}
          matchedPhotosTotal={allPhotos?.total}
          identity={activeIdentity}
        />
      </div>
      <GlobalWaldogramConfiguratorModal
        onDismiss={() => {
          closeModal(GLOBAL_WALDOGRAM_CONFIGURATOR_MODAL);
        }}
      />
      <ImagePreload urls={printBackgroundUrls} />
    </Fragment>
  );
};

AllPhotos.propTypes = propTypes;

const mapStateToProps = ({
  checkout,
  consumer: { memberships },
  matchedPhotos,
}) => ({
  activeIdentity: checkout.activeIdentity,
  cart: checkout.cart,
  albumMembershipGroupFilters: memberships.albumMembershipGroupFilters,
  matchedPhotoPrintBackgrounds: matchedPhotos.printBackgrounds,
});

const mapDispatchToProps = {
  openModal: openModalAction,
  closeModal: closeModalAction,
};

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  mapProps(({ data, activeIdentity, ...props }) => ({
    ...props,
    albumMembershipGroupId: props.match.params.albumMembershipGroupId,
    identityId: activeIdentity?.id,
  })),
  getAlbumMembershipGroupIdentity,
  mapProps(({ data, ...props }) => ({
    ...props,
    albumMembershipGroupIdentity: data?.albumMembershipGroup?.identities?.[0],
  })),
  getAlbumMembershipGroupWithAllPhotos,
  mapProps(({ data, ...props }) => ({
    ...props,
    albumMembershipGroup: data?.albumMembershipGroup,
    loading: data?.loading,
    loadMore: data?.albumMembershipGroup
      ? createLoadMoreMethod({
          source: data?.albumMembershipGroup,
          recordsPath: 'all_photos',
          fetchMore: data?.fetchMore,
        })
      : undefined,
  })),
  getAlbumMembershipGroupPurchases,
  mapProps(({ data, ...props }) => ({
    ...props,
    membershipEntitledPurchases: data?.albumMembershipGroup
      ? [
          ...(data?.albumMembershipGroup?.purchased_photos?.records ?? []).map(
            photo => photo.id,
          ),
          ...(
            data?.albumMembershipGroup?.purchased_photo_downloads?.records ?? []
          ).map(record => record.photo.id),
        ]
      : undefined,
  })),
  getAlbumForWebGalleriesFolder,
  mapProps(({ data, ...props }) => ({
    ...props,
    albumFolderId: data?.album?.root_album_folder?.id,
    loadingAlbumAlbumFolder: data?.loading,
  })),
  getAlbumForWebGalleriesPurchases,
  mapProps(({ data, ...props }) => ({
    ...props,
    album: data?.album,
    albumEntitledPurchases: data?.album
      ? [
          ...(data?.album?.purchased_photo_downloads?.records ?? []).map(
            record => record.photo.id,
          ),
        ]
      : undefined,
    loadingAlbum: data?.loading,
  })),
  getHierarchyItemForMatches,
  mapProps(({ data, ...props }) => ({
    ...props,
    hierarchyItem: data?.hierarchyItem,
    loadingHierarchyItem: data?.loading,
  })),
  getAlbumForWebGalleriesAllPhotos,
  mapProps(({ data, ...props }) => ({
    ...props,
    photos: data?.album?.photos,
    // Only add the loadMore method if we don't have one, otherwise
    // it's possible we replace the albumMembership's loadMore.
    loadMore:
      props?.loadMore ??
      createLoadMoreMethod({
        source: data?.album,
        recordsPath: 'photos',
        fetchMore: data?.fetchMore,
      }),
  })),
)(AllPhotos);
